import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ArrowRight, RefreshCw, ChevronDown, ChevronRight, MapPin, Calendar, Search, X, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import * as api from '../api';
import { useSociete } from '../context/SocieteContext';

// ─── Modal d'alerte zone sous douane ──────────────────────────────────────────
function DouaneWarningModal({ onConfirm, onCancel }) {
  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 9999,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
    }}>
      <div style={{
        background: 'white', borderRadius: 12, padding: 24, maxWidth: 420, width: '100%',
        border: '1px solid #FED7AA', boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
      }}>
        <div className="flex items-center gap-3 mb-3">
          <div style={{ background: '#FFF7ED', borderRadius: 8, padding: 8, flexShrink: 0 }}>
            <AlertTriangle size={20} style={{ color: '#C2410C' }} />
          </div>
          <h3 style={{ fontWeight: 700, fontSize: 15, color: 'var(--text)', margin: 0 }}>
            Zone sous douane
          </h3>
        </div>
        <p style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6, marginBottom: 20 }}>
          Les articles que vous déplacez sont stockés en <strong>zone sous douane</strong>.
          Assurez-vous que les formalités douanières ont été accomplies avant de procéder à ce mouvement.
        </p>
        <div className="flex gap-2 justify-end">
          <button onClick={onCancel}
            className="px-4 py-2 rounded-xl text-sm font-semibold transition-all"
            style={{ background: '#F1F5F9', color: 'var(--text-secondary)', border: '1px solid var(--border)' }}>
            Annuler
          </button>
          <button onClick={onConfirm} data-testid="douane-confirm-btn"
            className="px-4 py-2 rounded-xl text-sm font-semibold text-white transition-all hover:opacity-90"
            style={{ background: '#C2410C' }}>
            Confirmer le mouvement
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const TYPE_LABELS = {
  piece_detachee: 'Pièce',
  main_oeuvre:    'MO',
  prestation:     'Prestation',
  location:       'Location',
};

function TypeBadge({ type }) {
  if (type === 'materiel') return (
    <span className="inline-flex px-1.5 py-0.5 rounded text-xs font-medium"
      style={{ background: 'var(--primary-50)', color: 'var(--primary-light)' }}>Mat.</span>
  );
  const label = TYPE_LABELS[type] || type;
  return (
    <span className="inline-flex px-1.5 py-0.5 rounded text-xs font-medium"
      style={{ background: '#FFF7ED', color: '#C2410C' }}>{label}</span>
  );
}

// Dropdown zone cible — exclut la zone courante
function ZoneDestSelect({ value, onChange, zones, currentZoneId, disabled }) {
  const available = zones.filter(z => z.zone_id !== currentZoneId);
  return (
    <select
      value={value}
      onChange={e => onChange(e.target.value)}
      disabled={disabled}
      className="w-full text-xs border rounded px-2 py-1 transition-colors"
      style={{
        borderColor: value ? 'var(--primary-solid)' : 'var(--border)',
        background: disabled ? 'var(--bg-subtle)' : 'white',
        color: 'var(--text)',
        minWidth: 110,
      }}
    >
      <option value="">— Zone cible —</option>
      {currentZoneId !== null && (
        <option value="__none__">Sans zone</option>
      )}
      {available.map(z => (
        <option key={z.zone_id} value={z.zone_id}>{z.nom}</option>
      ))}
    </select>
  );
}

// ─── Page principale ───────────────────────────────────────────────────────────

export default function MouvementsZone() {
  const { activeAgences } = useSociete();
  const _ctxKey = activeAgences.map(a => a.agence_id).join(',');

  const today = new Date().toISOString().slice(0, 10);

  const [stocks, setStocks]               = useState([]);
  const [selectedStockId, setSelectedStockId] = useState('');
  const [dateMouvement, setDateMouvement] = useState(today);
  const [data, setData]                   = useState(null);
  const [loading, setLoading]             = useState(false);
  const [moving, setMoving]               = useState(false);
  const [movingKeys, setMovingKeys]       = useState(new Set());
  const [rowState, setRowState]           = useState({});
  const [collapsedZones, setCollapsedZones] = useState(new Set());

  // Filtres
  const [searchInput, setSearchInput]   = useState('');
  const [search, setSearch]             = useState('');         // valeur debounced
  const [filterZoneId, setFilterZoneId] = useState('');
  const [allZones, setAllZones]         = useState([]);         // zones stables pour le dropdown filtre
  const debounceRef = useRef(null);

  // Modal alerte douane
  const [douaneModal, setDouaneModal] = useState(null); // { resolve } ou null

  // Stocks filtrés sur l'agence active
  const filteredStocks = activeAgences.length > 0
    ? stocks.filter(s => activeAgences.some(a => a.agence_id === s.agence_id))
    : stocks;

  // ── Chargement dépôts ──────────────────────────────────────────────────────
  useEffect(() => {
    api.getStocks()
      .then(r => setStocks(r.data || []))
      .catch(() => toast.error('Erreur chargement dépôts'));
  }, []);

  // ── Reset sélection si le dépôt n'appartient plus à l'agence active ────────
  useEffect(() => {
    if (!selectedStockId) return;
    const still = activeAgences.length === 0
      || filteredStocks.some(s => s.stock_id === selectedStockId);
    if (!still) { setSelectedStockId(''); setData(null); setAllZones([]); }
  }, [_ctxKey]); // eslint-disable-line

  // ── Debounce sur la saisie de recherche ────────────────────────────────────
  const handleSearchChange = (val) => {
    setSearchInput(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setSearch(val), 400);
  };

  const clearFilters = () => {
    setSearchInput('');
    setSearch('');
    setFilterZoneId('');
    if (debounceRef.current) clearTimeout(debounceRef.current);
  };

  const hasFilters = searchInput || filterZoneId;

  // ── Chargement articles par zone ──────────────────────────────────────────
  const loadData = useCallback(async (opts = {}) => {
    if (!selectedStockId) return;
    setLoading(true);
    try {
      const params = {};
      const s = opts.search  !== undefined ? opts.search  : search;
      const z = opts.zone_id !== undefined ? opts.zone_id : filterZoneId;
      if (s) params.search   = s;
      if (z) params.zone_id  = z;

      const r = await api.getArticlesParZone(selectedStockId, params);
      setData(r.data);

      // Mémoriser les zones disponibles au premier chargement (sans filtre)
      if (!s && !z && r.data.zones_disponibles?.length) {
        setAllZones(r.data.zones_disponibles);
      }

      const init = {};
      (r.data.zones || []).forEach(zone => {
        zone.articles.forEach(a => {
          const key = `art-${a.entry_id}`;
          init[key] = { zoneDestId: '', qteADeplacer: a.quantite_disponible, checked: false };
        });
        zone.materiels.forEach(m => {
          const key = `mat-${m.materiel_id}`;
          init[key] = { zoneDestId: '', checked: false };
        });
      });
      setRowState(init);
    } catch {
      toast.error('Erreur chargement articles');
    } finally {
      setLoading(false);
    }
  }, [selectedStockId, search, filterZoneId]);

  // Rechargement initial (dépôt changé)
  useEffect(() => {
    if (selectedStockId) {
      clearFilters();
      loadData({ search: '', zone_id: '' });
    }
  }, [selectedStockId]); // eslint-disable-line

  // Rechargement quand les filtres debounced changent
  useEffect(() => {
    if (selectedStockId) loadData();
  }, [search, filterZoneId]); // eslint-disable-line

  // ── Helpers recherche de ligne ─────────────────────────────────────────────
  const findMaterielRow = (matId) => {
    for (const zone of data?.zones || []) {
      const found = zone.materiels.find(m => m.materiel_id === matId);
      if (found) return { ...found, zone_id: zone.zone_id };
    }
    return null;
  };

  const findArticleRow = (entryId) => {
    for (const zone of data?.zones || []) {
      const found = zone.articles.find(a => a.entry_id === entryId);
      if (found) return { ...found, zone_id: zone.zone_id };
    }
    return null;
  };

  // ── Mise à jour état ligne ─────────────────────────────────────────────────
  const updateRow = (key, patch) =>
    setRowState(prev => ({ ...prev, [key]: { ...prev[key], ...patch } }));

  // ── Garde douane ──────────────────────────────────────────────────────────
  // Retourne true si l'utilisateur confirme, false s'il annule
  const checkDouane = (sourceZoneId) => {
    const zonesDispos = allZones.length > 0 ? allZones : (data?.zones_disponibles || []);
    const zone = zonesDispos.find(z => z.zone_id === sourceZoneId);
    if (!zone?.is_douane) return Promise.resolve(true);
    return new Promise(resolve => setDouaneModal({ resolve }));
  };

  // ── Déplacer une seule ligne ───────────────────────────────────────────────
  const handleMove = async (key, zoneDestId, isMateriel) => {
    if (!zoneDestId) return toast.error('Sélectionnez une zone cible');
    const destId = zoneDestId === '__none__' ? null : zoneDestId;

    // Déterminer la zone source pour la vérification douane
    let sourceZoneId = null;
    if (isMateriel) {
      const mat = findMaterielRow(key.replace('mat-', ''));
      sourceZoneId = mat?.zone_id ?? null;
    } else {
      const art = findArticleRow(key.replace('art-', ''));
      sourceZoneId = art?.zone_id ?? null;
    }
    const confirmed = await checkDouane(sourceZoneId);
    if (!confirmed) return;

    setMovingKeys(prev => new Set(prev).add(key));
    try {
      if (isMateriel) {
        const matId = key.replace('mat-', '');
        const mat = findMaterielRow(matId);
        if (!mat) return;
        await api.deplacerStock({
          stock_source_id: selectedStockId,
          stock_dest_id:   selectedStockId,
          zone_source_id:  mat.zone_id,
          zone_dest_id:    destId,
          materiel_ids:    [matId],
          date_mouvement:  dateMouvement,
        });
      } else {
        const entryId = key.replace('art-', '');
        const art = findArticleRow(entryId);
        if (!art) return;
        const qte = rowState[key]?.qteADeplacer ?? art.quantite_disponible;
        await api.deplacerStock({
          stock_source_id: selectedStockId,
          stock_dest_id:   selectedStockId,
          zone_source_id:  art.zone_id,
          zone_dest_id:    destId,
          article_id:      art.article_id,
          quantite:        Number(qte) || 1,
          date_mouvement:  dateMouvement,
        });
      }
      toast.success('Déplacé avec succès');
      await loadData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur lors du déplacement');
    } finally {
      setMovingKeys(prev => { const s = new Set(prev); s.delete(key); return s; });
    }
  };

  // ── Déplacer la sélection ──────────────────────────────────────────────────
  const handleMoveSelection = async () => {
    const selected = Object.entries(rowState).filter(([, rs]) => rs.checked && rs.zoneDestId);
    if (selected.length === 0) return toast.error('Sélectionnez des lignes avec une zone cible');

    // Vérifier si au moins une zone source est sous douane
    const zonesDispos = allZones.length > 0 ? allZones : (data?.zones_disponibles || []);
    const hasDouane = selected.some(([key]) => {
      let zoneId = null;
      if (key.startsWith('mat-')) {
        const mat = findMaterielRow(key.replace('mat-', ''));
        zoneId = mat?.zone_id ?? null;
      } else {
        const art = findArticleRow(key.replace('art-', ''));
        zoneId = art?.zone_id ?? null;
      }
      return zonesDispos.find(z => z.zone_id === zoneId)?.is_douane;
    });

    if (hasDouane) {
      const confirmed = await new Promise(resolve => setDouaneModal({ resolve }));
      if (!confirmed) return;
    }

    setMoving(true);
    let ok = 0; let err = 0;

    const matGroups = {};
    const artRows   = [];

    for (const [key, rs] of selected) {
      const destId = rs.zoneDestId === '__none__' ? null : rs.zoneDestId;
      if (key.startsWith('mat-')) {
        const matId = key.replace('mat-', '');
        const matRow = findMaterielRow(matId);
        if (!matRow) continue;
        const groupKey = destId ?? '__none__';
        if (!matGroups[groupKey]) matGroups[groupKey] = { destId, mat_ids: [] };
        matGroups[groupKey].mat_ids.push(matId);
      } else {
        artRows.push({ key, rs, destId });
      }
    }

    for (const group of Object.values(matGroups)) {
      try {
        await api.deplacerStock({
          stock_source_id: selectedStockId,
          stock_dest_id:   selectedStockId,
          zone_source_id:  null,
          zone_dest_id:    group.destId,
          materiel_ids:    group.mat_ids,
          date_mouvement:  dateMouvement,
        });
        ok += group.mat_ids.length;
      } catch { err += group.mat_ids.length; }
    }

    for (const { key, rs, destId } of artRows) {
      const entryId = key.replace('art-', '');
      const art = findArticleRow(entryId);
      if (!art) continue;
      try {
        await api.deplacerStock({
          stock_source_id: selectedStockId,
          stock_dest_id:   selectedStockId,
          zone_source_id:  art.zone_id,
          zone_dest_id:    destId,
          article_id:      art.article_id,
          quantite:        Number(rs.qteADeplacer) || 1,
          date_mouvement:  dateMouvement,
        });
        ok++;
      } catch { err++; }
    }

    if (ok > 0) toast.success(`${ok} article(s) déplacé(s)`);
    if (err > 0) toast.error(`${err} échec(s)`);
    await loadData();
    setMoving(false);
  };

  // ── Sélection globale / par zone ───────────────────────────────────────────
  const allKeys = Object.keys(rowState);
  const checkedKeys = allKeys.filter(k => rowState[k]?.checked);
  const checkedCount = checkedKeys.length;

  const toggleAll = () => {
    const allChecked = checkedCount === allKeys.length;
    setRowState(prev =>
      Object.fromEntries(Object.entries(prev).map(([k, v]) => [k, { ...v, checked: !allChecked }]))
    );
  };

  const toggleZone = (zone) => {
    const keys = [
      ...zone.articles.map(a => `art-${a.entry_id}`),
      ...zone.materiels.map(m => `mat-${m.materiel_id}`),
    ];
    const allChecked = keys.every(k => rowState[k]?.checked);
    setRowState(prev => {
      const next = { ...prev };
      keys.forEach(k => { if (next[k]) next[k] = { ...next[k], checked: !allChecked }; });
      return next;
    });
  };

  const toggleCollapseZone = (zoneKey) =>
    setCollapsedZones(prev => {
      const s = new Set(prev);
      s.has(zoneKey) ? s.delete(zoneKey) : s.add(zoneKey);
      return s;
    });

  const zonesDispos = allZones.length > 0 ? allZones : (data?.zones_disponibles || []);
  const totalRows = (data?.zones || []).reduce((s, z) => s + z.articles.length + z.materiels.length, 0);
  const totalArticles = data?.total_articles ?? 0;
  const totalMateriels = data?.total_materiels ?? 0;
  const isFiltered = data?.filtered ?? false;
  const hitLimit = isFiltered === false && totalRows >= (data?.limit ?? 300);

  // ─── Rendu ─────────────────────────────────────────────────────────────────
  return (
    <div className="flex flex-col h-full" style={{ background: 'var(--bg-page)', minHeight: '100vh' }}>
      {/* Modal zone sous douane */}
      {douaneModal && (
        <DouaneWarningModal
          onConfirm={() => { setDouaneModal(null); douaneModal.resolve(true); }}
          onCancel={() => { setDouaneModal(null); douaneModal.resolve(false); }}
        />
      )}

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="px-6 py-5 border-b" style={{ borderColor: 'var(--border)', background: 'var(--card)' }}>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold" style={{ color: 'var(--text)' }}>Mouvements de stock</h1>
            <p className="text-sm mt-0.5" style={{ color: 'var(--text-muted)' }}>
              Déplacez des articles et matériels d'une zone à une autre
            </p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            {/* Sélecteur dépôt */}
            <div className="relative">
              <select
                value={selectedStockId}
                onChange={e => { setSelectedStockId(e.target.value); setData(null); setAllZones([]); }}
                data-testid="stock-selector"
                className="appearance-none pl-3 pr-8 py-2 rounded-lg text-sm border font-medium transition-all"
                style={{
                  borderColor: 'var(--border)',
                  background: 'var(--card)',
                  color: 'var(--text)',
                  minWidth: 200,
                }}
              >
                <option value="">— Choisir un dépôt —</option>
                {filteredStocks.map(s => (
                  <option key={s.stock_id} value={s.stock_id}>
                    {s.nom}{s.agence_nom ? ` (${s.agence_nom})` : ''}
                  </option>
                ))}
              </select>
              <ChevronDown size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none"
                style={{ color: 'var(--text-muted)' }} />
            </div>

            {/* Date du mouvement */}
            <div className="relative flex items-center gap-1.5">
              <Calendar size={14} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
              <input
                type="date"
                value={dateMouvement}
                onChange={e => setDateMouvement(e.target.value)}
                data-testid="date-mouvement-picker"
                className="text-sm border rounded-lg px-2 py-2 transition-all"
                style={{
                  borderColor: 'var(--border)',
                  background: 'var(--card)',
                  color: 'var(--text)',
                }}
              />
            </div>

            {/* Bouton rafraîchir */}
            {selectedStockId && (
              <button
                onClick={() => loadData()}
                disabled={loading}
                data-testid="refresh-btn"
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm border transition-all"
                style={{ borderColor: 'var(--border)', color: 'var(--text-muted)', background: 'var(--card)' }}
              >
                <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
                Actualiser
              </button>
            )}

            {/* Déplacer la sélection */}
            {checkedCount > 0 && (
              <button
                onClick={handleMoveSelection}
                disabled={moving}
                data-testid="move-selection-btn"
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all disabled:opacity-60"
                style={{ background: 'var(--primary-solid)', color: 'white' }}
              >
                {moving ? (
                  <span className="w-3.5 h-3.5 border-2 rounded-full animate-spin flex-shrink-0"
                    style={{ borderColor: 'rgba(255,255,255,0.4)', borderTopColor: 'white' }} />
                ) : (
                  <ArrowRight size={15} />
                )}
                Déplacer la sélection ({checkedCount})
              </button>
            )}
          </div>
        </div>

        {/* ── Barre de filtres (visible uniquement si un dépôt est sélectionné) ── */}
        {selectedStockId && (
          <div className="flex items-center gap-3 mt-4 flex-wrap">
            {/* Recherche réf / désignation */}
            <div className="relative flex-1" style={{ minWidth: 220, maxWidth: 380 }}>
              <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none"
                style={{ color: 'var(--text-muted)' }} />
              <input
                type="text"
                value={searchInput}
                onChange={e => handleSearchChange(e.target.value)}
                placeholder="Réf., désignation, n° série…"
                data-testid="search-input"
                className="w-full pl-7 pr-7 py-1.5 text-xs border rounded-lg transition-all"
                style={{
                  borderColor: searchInput ? 'var(--primary-solid)' : 'var(--border)',
                  background: 'var(--card)',
                  color: 'var(--text)',
                }}
              />
              {searchInput && (
                <button onClick={() => handleSearchChange('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2"
                  style={{ color: 'var(--text-muted)' }}>
                  <X size={12} />
                </button>
              )}
            </div>

            {/* Filtre par zone */}
            <div className="relative" style={{ minWidth: 180 }}>
              <MapPin size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none"
                style={{ color: 'var(--text-muted)' }} />
              <select
                value={filterZoneId}
                onChange={e => setFilterZoneId(e.target.value)}
                data-testid="filter-zone-select"
                className="appearance-none w-full pl-7 pr-6 py-1.5 text-xs border rounded-lg transition-all"
                style={{
                  borderColor: filterZoneId ? 'var(--primary-solid)' : 'var(--border)',
                  background: 'var(--card)',
                  color: 'var(--text)',
                }}
              >
                <option value="">Toutes les zones</option>
                <option value="__none__">Sans zone</option>
                {zonesDispos.map(z => (
                  <option key={z.zone_id} value={z.zone_id}>{z.nom}</option>
                ))}
              </select>
              <ChevronDown size={12} className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none"
                style={{ color: 'var(--text-muted)' }} />
            </div>

            {/* Effacer les filtres */}
            {hasFilters && (
              <button
                onClick={clearFilters}
                data-testid="clear-filters-btn"
                className="flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg border transition-all"
                style={{ borderColor: 'var(--border)', color: 'var(--text-muted)', background: 'var(--card)' }}
              >
                <X size={11} /> Effacer
              </button>
            )}

            {/* Compteur résultats */}
            {data && (
              <span className="text-xs ml-auto" style={{ color: 'var(--text-muted)' }}>
                {isFiltered
                  ? `${totalRows} résultat${totalRows > 1 ? 's' : ''}`
                  : hitLimit
                    ? `${totalRows} lignes (limite atteinte — utilisez un filtre)`
                    : `${totalArticles} article${totalArticles > 1 ? 's' : ''} · ${totalMateriels} matériel${totalMateriels > 1 ? 's' : ''}`
                }
              </span>
            )}
          </div>
        )}
      </div>

      {/* ── Contenu ─────────────────────────────────────────────────────────── */}
      <div className="flex-1 overflow-auto p-6">

        {!selectedStockId && (
          <div className="flex flex-col items-center justify-center py-24 gap-3"
            style={{ color: 'var(--text-muted)' }}>
            <MapPin size={40} style={{ opacity: 0.3 }} />
            <p className="text-sm">Sélectionnez un dépôt pour afficher ses articles</p>
          </div>
        )}

        {selectedStockId && loading && (
          <div className="flex items-center justify-center py-24 gap-3"
            style={{ color: 'var(--text-muted)' }}>
            <span className="w-5 h-5 border-2 rounded-full animate-spin flex-shrink-0"
              style={{ borderColor: 'var(--border)', borderTopColor: 'var(--primary-solid)' }} />
            <span className="text-sm">Chargement…</span>
          </div>
        )}

        {selectedStockId && !loading && data && totalRows === 0 && (
          <div className="flex flex-col items-center justify-center py-24 gap-2"
            style={{ color: 'var(--text-muted)' }}>
            <MapPin size={36} style={{ opacity: 0.25 }} />
            <p className="text-sm">Aucun article en stock dans ce dépôt</p>
          </div>
        )}

        {selectedStockId && !loading && data && totalRows > 0 && (
          <div className="rounded-xl border overflow-hidden"
            style={{ borderColor: 'var(--border)', background: 'var(--card)' }}>
            <table className="w-full text-xs border-collapse">
              <thead>
                <tr style={{ background: 'var(--bg-subtle)', borderBottom: '1px solid var(--border)' }}>
                  <th className="px-3 py-2.5 w-8">
                    <input
                      type="checkbox"
                      data-testid="select-all-checkbox"
                      checked={allKeys.length > 0 && checkedCount === allKeys.length}
                      onChange={toggleAll}
                      className="rounded"
                    />
                  </th>
                  <th className="px-3 py-2.5 text-left font-semibold w-10"
                    style={{ color: 'var(--text-muted)' }}>Type</th>
                  <th className="px-3 py-2.5 text-left font-semibold w-28"
                    style={{ color: 'var(--text-muted)' }}>Réf.</th>
                  <th className="px-3 py-2.5 text-left font-semibold"
                    style={{ color: 'var(--text-muted)' }}>Désignation</th>
                  <th className="px-3 py-2.5 text-left font-semibold w-28"
                    style={{ color: 'var(--text-muted)' }}>Marque</th>
                  <th className="px-3 py-2.5 text-left font-semibold w-28"
                    style={{ color: 'var(--text-muted)' }}>Modèle</th>
                  <th className="px-3 py-2.5 text-left font-semibold w-32"
                    style={{ color: 'var(--text-muted)' }}>N° série</th>
                  <th className="px-3 py-2.5 text-right font-semibold w-16"
                    style={{ color: 'var(--text-muted)' }}>Qté</th>
                  <th className="px-3 py-2.5 text-left font-semibold w-36"
                    style={{ color: 'var(--text-muted)' }}>Zone cible</th>
                  <th className="px-2 py-2.5 w-20" />
                </tr>
              </thead>

              <tbody>
                {(data.zones || []).map(zone => {
                  const zoneKey = zone.zone_id ?? '__none__';
                  const isCollapsed = collapsedZones.has(zoneKey);
                  const zoneRowCount = zone.articles.length + zone.materiels.length;
                  const zoneKeys = [
                    ...zone.articles.map(a => `art-${a.entry_id}`),
                    ...zone.materiels.map(m => `mat-${m.materiel_id}`),
                  ];
                  const zoneAllChecked = zoneKeys.every(k => rowState[k]?.checked);

                  return (
                    <React.Fragment key={zoneKey}>
                      {/* En-tête de zone */}
                      <tr style={{ background: 'var(--bg-subtle)', borderBottom: '1px solid var(--border)' }}>
                        <td className="px-3 py-2">
                          <input type="checkbox"
                            checked={zoneAllChecked && zoneKeys.length > 0}
                            onChange={() => toggleZone(zone)}
                            className="rounded"
                          />
                        </td>
                        <td colSpan={9} className="px-2 py-2">
                          <button
                            onClick={() => toggleCollapseZone(zoneKey)}
                            className="flex items-center gap-2 font-semibold text-xs w-full text-left"
                            style={{ color: 'var(--text-secondary)' }}
                          >
                            {isCollapsed
                              ? <ChevronRight size={13} />
                              : <ChevronDown size={13} />}
                            <MapPin size={12} style={{ color: 'var(--primary-solid)', flexShrink: 0 }} />
                            {zone.zone_nom || 'Sans zone'}
                            <span className="font-normal ml-1" style={{ color: 'var(--text-muted)' }}>
                              — {zoneRowCount} ligne{zoneRowCount > 1 ? 's' : ''}
                            </span>
                          </button>
                        </td>
                      </tr>

                      {!isCollapsed && (
                        <>
                          {/* Lignes articles consommables */}
                          {zone.articles.map(art => {
                            const key = `art-${art.entry_id}`;
                            const rs = rowState[key] || { zoneDestId: '', qteADeplacer: art.quantite_disponible, checked: false };
                            const isMoving = movingKeys.has(key);
                            return (
                              <tr key={key} data-testid={`row-art-${art.entry_id}`}
                                style={{
                                  borderBottom: '1px solid var(--border-light)',
                                  background: rs.checked ? 'var(--primary-50)' : 'transparent',
                                }}>
                                <td className="px-3 py-2">
                                  <input type="checkbox"
                                    checked={rs.checked}
                                    onChange={e => updateRow(key, { checked: e.target.checked })}
                                    className="rounded"
                                  />
                                </td>
                                <td className="px-3 py-2">
                                  <TypeBadge type={art.article_type} />
                                </td>
                                <td className="px-3 py-2 font-mono" style={{ color: 'var(--text-muted)' }}>
                                  {art.article_ref || '—'}
                                </td>
                                <td className="px-3 py-2 font-medium" style={{ color: 'var(--text)' }}>
                                  {art.article_nom}
                                </td>
                                <td className="px-3 py-2" style={{ color: 'var(--text-muted)' }}>
                                  {art.marque_nom || '—'}
                                </td>
                                <td className="px-3 py-2" style={{ color: 'var(--text-muted)' }}>—</td>
                                <td className="px-3 py-2" style={{ color: 'var(--text-muted)' }}>—</td>
                                <td className="px-3 py-2 text-right">
                                  <input
                                    type="number"
                                    min="0.001"
                                    max={art.quantite_disponible}
                                    step="1"
                                    value={rs.qteADeplacer ?? art.quantite_disponible}
                                    onChange={e => updateRow(key, { qteADeplacer: e.target.value })}
                                    data-testid={`qty-art-${art.entry_id}`}
                                    className="w-16 text-right text-xs border rounded px-1.5 py-0.5 font-semibold"
                                    style={{ borderColor: 'var(--border)', color: 'var(--text)' }}
                                  />
                                </td>
                                <td className="px-2 py-1.5">
                                  <ZoneDestSelect
                                    value={rs.zoneDestId}
                                    onChange={v => updateRow(key, { zoneDestId: v })}
                                    zones={zonesDispos}
                                    currentZoneId={zone.zone_id}
                                    disabled={isMoving}
                                  />
                                </td>
                                <td className="px-2 py-1.5 text-right">
                                  <button
                                    onClick={() => handleMove(key, rs.zoneDestId, false)}
                                    disabled={!rs.zoneDestId || isMoving || moving}
                                    data-testid={`move-art-${art.entry_id}`}
                                    title="Déplacer"
                                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold transition-all disabled:opacity-40"
                                    style={{ background: 'var(--primary-solid)', color: 'white' }}
                                  >
                                    {isMoving
                                      ? <span className="w-3 h-3 border-2 rounded-full animate-spin flex-shrink-0"
                                          style={{ borderColor: 'rgba(255,255,255,0.4)', borderTopColor: 'white' }} />
                                      : <ArrowRight size={12} />}
                                  </button>
                                </td>
                              </tr>
                            );
                          })}

                          {/* Lignes matériels sérialisés */}
                          {zone.materiels.map(mat => {
                            const key = `mat-${mat.materiel_id}`;
                            const rs = rowState[key] || { zoneDestId: '', checked: false };
                            const isMoving = movingKeys.has(key);
                            return (
                              <tr key={key} data-testid={`row-mat-${mat.materiel_id}`}
                                style={{
                                  borderBottom: '1px solid var(--border-light)',
                                  background: rs.checked ? 'var(--primary-50)' : 'transparent',
                                }}>
                                <td className="px-3 py-2">
                                  <input type="checkbox"
                                    checked={rs.checked}
                                    onChange={e => updateRow(key, { checked: e.target.checked })}
                                    className="rounded"
                                  />
                                </td>
                                <td className="px-3 py-2">
                                  <TypeBadge type="materiel" />
                                </td>
                                <td className="px-3 py-2 font-mono" style={{ color: 'var(--text-muted)' }}>
                                  {mat.article_ref || '—'}
                                </td>
                                <td className="px-3 py-2 font-medium" style={{ color: 'var(--text)' }}>
                                  {mat.article_nom}
                                  {mat.numero_parc && (
                                    <span className="ml-1.5 text-xs font-normal" style={{ color: 'var(--text-muted)' }}>
                                      #{mat.numero_parc}
                                    </span>
                                  )}
                                </td>
                                <td className="px-3 py-2" style={{ color: 'var(--text-muted)' }}>
                                  {mat.marque || '—'}
                                </td>
                                <td className="px-3 py-2" style={{ color: 'var(--text)' }}>
                                  {mat.modele || '—'}
                                </td>
                                <td className="px-3 py-2 font-mono text-xs" style={{ color: 'var(--text)' }}>
                                  {mat.numero_serie || '—'}
                                </td>
                                <td className="px-3 py-2 text-right tabular-nums" style={{ color: 'var(--text-muted)' }}>
                                  1
                                </td>
                                <td className="px-2 py-1.5">
                                  <ZoneDestSelect
                                    value={rs.zoneDestId}
                                    onChange={v => updateRow(key, { zoneDestId: v })}
                                    zones={zonesDispos}
                                    currentZoneId={zone.zone_id}
                                    disabled={isMoving}
                                  />
                                </td>
                                <td className="px-2 py-1.5 text-right">
                                  <button
                                    onClick={() => handleMove(key, rs.zoneDestId, true)}
                                    disabled={!rs.zoneDestId || isMoving || moving}
                                    data-testid={`move-mat-${mat.materiel_id}`}
                                    title="Déplacer"
                                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold transition-all disabled:opacity-40"
                                    style={{ background: 'var(--primary-solid)', color: 'white' }}
                                  >
                                    {isMoving
                                      ? <span className="w-3 h-3 border-2 rounded-full animate-spin flex-shrink-0"
                                          style={{ borderColor: 'rgba(255,255,255,0.4)', borderTopColor: 'white' }} />
                                      : <ArrowRight size={12} />}
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                        </>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
